import type { QueueTemplate } from '../types/queue';

// --- SYSTEM TEMPLATES (CANONICAL v2026.3) ---
export const QUEUE_TEMPLATES: QueueTemplate[] = [
    {
        id: 'Q_IC_01',
        name: 'My Open Work',
        audienceRole: 'Individual Contributor',
        intentCategory: 'my_work',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Assigned to current user AND Resolution IS EMPTY',
        description: 'Primary personal execution queue. Works across all work types and avoids brittle status dependencies.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'issuetype', label: 'Type'}, {field: 'status', label: 'Status'}, {field: 'created', label: 'Created'}],
        defaultSort: 'created DESC',
        defaultJql: 'assignee = currentUser() AND resolution = Unresolved'
    },
    {
        id: 'Q_TC_01',
        name: 'Team Active Work',
        audienceRole: 'Team Member / Team Lead',
        intentCategory: 'team_work',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Assigned to team AND Resolution IS EMPTY',
        description: 'Shared operational view for teams working from the same Space regardless of work type.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'status', label: 'Status'}, {field: 'priority', label: 'Priority'}],
        defaultSort: 'priority DESC, created ASC',
        defaultJql: 'assignee in membersOf(projectRole("Service Desk Team")) AND resolution = Unresolved'
    },
    // --- POST-MVP EXPANSION (v2026.3+) ---
    // Individual Contributor
    {
        id: 'Q_IC_02',
        name: 'My High-Priority Work',
        audienceRole: 'Individual Contributor',
        intentCategory: 'my_work',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Assigned to me AND Priority = High/Critical AND Unresolved',
        description: 'Focuses on the most urgent tasks assigned to the current user.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'priority', label: 'Priority'}, {field: 'status', label: 'Status'}],
        defaultSort: 'priority DESC, created ASC',
        defaultJql: 'assignee = currentUser() AND priority in (High, Critical) AND resolution = Unresolved'
    },
    {
        id: 'Q_IC_03',
        name: 'Work Waiting on Me',
        audienceRole: 'Individual Contributor',
        intentCategory: 'my_work',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Assigned to me AND Status indicates action required AND Unresolved',
        description: 'Items where the user needs to take the next action.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'status', label: 'Status'}, {field: 'updated', label: 'Last Updated'}],
        defaultSort: 'updated ASC',
        defaultJql: 'assignee = currentUser() AND resolution = Unresolved'
    },
    {
        id: 'Q_IC_04',
        name: 'My Recently Reopened Work',
        audienceRole: 'Individual Contributor',
        intentCategory: 'my_work',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Assigned to me AND Status changed from Done to Open recently AND Unresolved',
        description: 'Tracks items that were sent back or reopened, requiring immediate attention.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'status', label: 'Status'}, {field: 'updated', label: 'Reopened'}],
        defaultSort: 'updated DESC',
        defaultJql: 'assignee = currentUser() AND status WAS "Done" AFTER -7d AND resolution = Unresolved'
    },
    // Team Lead
    {
        id: 'Q_TL_02',
        name: 'Team SLA Breach Risk',
        audienceRole: 'Team Lead',
        intentCategory: 'team_work',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Team assigned AND Unresolved AND SLA < threshold',
        description: 'Items at risk of breaching SLA targets within the team.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'sla_time_left', label: 'Time Left'}],
        defaultSort: 'sla_time_left ASC',
        defaultJql: 'assignee in membersOf(projectRole("Service Desk Team")) AND resolution = Unresolved AND slaFunction("atRisk")'
    },
    {
        id: 'Q_TL_03',
        name: 'Unassigned Team Work',
        audienceRole: 'Team Lead',
        intentCategory: 'team_work',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Unassigned AND Team Context AND Unresolved',
        description: 'Work that needs to be triaged and assigned to team members.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'created', label: 'Created'}, {field: 'priority', label: 'Priority'}],
        defaultSort: 'created ASC',
        defaultJql: 'assignee IS EMPTY AND resolution = Unresolved'
    },
    {
        id: 'Q_TL_04',
        name: 'Aging Team Work',
        audienceRole: 'Team Lead',
        intentCategory: 'team_work',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Team assigned AND Unresolved AND Created < -X days',
        description: 'Stale items that have been open for an extended period.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'created', label: 'Created'}],
        defaultSort: 'created ASC',
        defaultJql: 'assignee in membersOf(projectRole("Service Desk Team")) AND resolution = Unresolved AND created < -14d'
    },
    {
        id: 'Q_TL_05',
        name: 'Work Blocked or Waiting',
        audienceRole: 'Team Lead',
        intentCategory: 'team_work',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Team assigned AND Unresolved AND Flagged as Impediment',
        description: 'Items blocked by external factors or dependencies.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'status', label: 'Status'}],
        defaultSort: 'updated DESC',
        defaultJql: 'assignee in membersOf(projectRole("Service Desk Team")) AND resolution = Unresolved AND flagged = Impediment'
    },
    // Incident Leadership
    {
        id: 'Q_INC_01',
        name: 'Major Incidents',
        audienceRole: 'Incident Commander / On-call',
        intentCategory: 'breaches',
        domain: 'incident',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Work Type = Incident AND Major Incident Flag = true AND Resolution IS EMPTY',
        description: 'Ensures high-visibility tracking of active major incidents.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'status', label: 'Status'}, {field: 'severity', label: 'Severity'}, {field: 'created', label: 'Started'}],
        defaultSort: 'updated DESC',
        defaultJql: 'issuetype = Incident AND "Major Incident" is not EMPTY AND resolution = Unresolved'
    },
    {
        id: 'Q_INC_02',
        name: 'SLA Breach Imminent (Incidents)',
        audienceRole: 'Incident Manager',
        intentCategory: 'breaches',
        domain: 'incident',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Work Type = Incident AND Resolution IS EMPTY AND SLA remaining time < threshold',
        description: 'Proactive breach prevention for critical incident SLAs.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'sla_time_left', label: 'Time Left'}],
        defaultSort: 'sla_time_left ASC',
        defaultJql: 'issuetype = Incident AND resolution = Unresolved AND slaFunction("atRisk")'
    },
    {
        id: 'Q_INC_03',
        name: 'Active Major Incidents',
        audienceRole: 'Incident Leadership',
        intentCategory: 'breaches',
        domain: 'incident',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Major Incident Flag = Yes AND Unresolved',
        description: 'High-visibility dashboard for ongoing major incidents.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'severity', label: 'Severity'}, {field: 'status', label: 'Status'}],
        defaultSort: 'severity DESC, updated DESC',
        defaultJql: 'issuetype = Incident AND "Major Incident" is not EMPTY AND resolution = Unresolved'
    },
    {
        id: 'Q_INC_04',
        name: 'Incident Escalation Candidates',
        audienceRole: 'Incident Leadership',
        intentCategory: 'breaches',
        domain: 'incident',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Incident AND Unresolved AND High Update Volume',
        description: 'Incidents that may require intervention due to high activity or duration.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'updated', label: 'Last Update'}],
        defaultSort: 'updated DESC',
        defaultJql: 'issuetype = Incident AND resolution = Unresolved AND updated > -1h'
    },
    {
        id: 'Q_INC_05',
        name: 'Repeating Incidents',
        audienceRole: 'Incident Leadership',
        intentCategory: 'risk',
        domain: 'incident',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Incident AND Unresolved AND Linked to Previous Incidents',
        description: 'Potential problem candidates identified by recurrence.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'created', label: 'Created'}],
        defaultSort: 'created DESC',
        defaultJql: 'issuetype = Incident AND resolution = Unresolved AND labels = "recurring"'
    },
    // Change & Governance
    {
        id: 'Q_CHG_01',
        name: 'Pending Change Approvals',
        audienceRole: 'CAB / Approvers',
        intentCategory: 'governance',
        domain: 'change',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Work Type = Change AND Approval Status = Pending AND Resolution IS EMPTY',
        description: 'Centralized approval workload for CAB members.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'risk', label: 'Risk'}, {field: 'planned_start', label: 'Start'}, {field: 'assignee', label: 'Owner'}],
        defaultSort: 'planned_start ASC',
        defaultJql: 'issuetype = Change AND approval = pendingBy(currentUser()) AND resolution = Unresolved'
    },
    {
        id: 'Q_CHG_02',
        name: 'Emergency Changes In Progress',
        audienceRole: 'Change Manager',
        intentCategory: 'governance',
        domain: 'change',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Change Type = Emergency AND Unresolved',
        description: 'Critical changes bypassing standard approval flows.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Owner'}, {field: 'status', label: 'Status'}],
        defaultSort: 'created DESC',
        defaultJql: 'issuetype = Change AND priority = Critical AND resolution = Unresolved'
    },
    {
        id: 'Q_CHG_03',
        name: 'Failed or Rolled-Back Changes',
        audienceRole: 'Change Manager',
        intentCategory: 'risk',
        domain: 'change',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Change AND Resolution in (Failed, Rolled Back)',
        description: 'Review queue for failed changes requiring post-implementation review.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Owner'}, {field: 'resolution', label: 'Outcome'}],
        defaultSort: 'updated DESC',
        defaultJql: 'issuetype = Change AND resolution in (Failed, "Rolled Back")'
    },
    {
        id: 'Q_CHG_04',
        name: 'Changes Near Blackout Windows',
        audienceRole: 'Change Manager',
        intentCategory: 'risk',
        domain: 'change',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Change AND Unresolved AND Planned Start near Blackout',
        description: 'Risk management view for changes scheduled near freeze periods.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'planned_start', label: 'Start'}, {field: 'risk', label: 'Risk'}],
        defaultSort: 'planned_start ASC',
        defaultJql: 'issuetype = Change AND resolution = Unresolved AND planned_start < endOfWeek()'
    },
    // Problem Management
    {
        id: 'Q_PRB_01',
        name: 'Open Problems',
        audienceRole: 'Problem Manager',
        intentCategory: 'risk',
        domain: 'problem',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Problem AND Unresolved',
        description: 'Backlog of active problem investigations.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'status', label: 'Status'}],
        defaultSort: 'created DESC',
        defaultJql: 'issuetype = Problem AND resolution = Unresolved'
    },
    {
        id: 'Q_PRB_02',
        name: 'Problems Without RCA',
        audienceRole: 'Problem Manager',
        intentCategory: 'risk',
        domain: 'problem',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Problem AND Unresolved AND RCA Field Empty',
        description: 'Problems missing Root Cause Analysis data.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'created', label: 'Created'}],
        defaultSort: 'created ASC',
        defaultJql: 'issuetype = Problem AND resolution = Unresolved AND "Root Cause" is EMPTY'
    },
    {
        id: 'Q_PRB_03',
        name: 'Problems With Workarounds Only',
        audienceRole: 'Problem Manager',
        intentCategory: 'risk',
        domain: 'problem',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Problem AND Unresolved AND Workaround Present AND RCA Empty',
        description: 'Problems stabilized by workaround but not permanently fixed.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'status', label: 'Status'}],
        defaultSort: 'created ASC',
        defaultJql: 'issuetype = Problem AND resolution = Unresolved AND "Workaround" is not EMPTY AND "Root Cause" is EMPTY'
    },
    // Management & Executive
    {
        id: 'Q_MGR_01',
        name: 'Manager Oversight',
        audienceRole: 'Service Desk Manager',
        intentCategory: 'management',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Resolution IS EMPTY grouped by Assignee and Priority',
        description: 'Enables workload balancing, coaching, and bottleneck detection.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'priority', label: 'Priority'}, {field: 'status', label: 'Status'}],
        defaultSort: 'assignee ASC, priority DESC',
        defaultJql: 'resolution = Unresolved ORDER BY assignee ASC, priority DESC'
    },
    {
        id: 'Q_MGR_02',
        name: 'Workload Distribution',
        audienceRole: 'Manager',
        intentCategory: 'management',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Unresolved grouped by Assignee',
        description: 'View for balancing ticket load across the team.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'status', label: 'Status'}],
        defaultSort: 'assignee ASC',
        defaultJql: 'resolution = Unresolved ORDER BY assignee ASC'
    },
    {
        id: 'Q_MGR_03',
        name: 'SLA Performance Overview',
        audienceRole: 'Manager',
        intentCategory: 'management',
        domain: 'cross_domain',
        maturityLevel: 'basic',
        canonicalFilterLogic: 'Unresolved AND SLA Data Present',
        description: 'High-level view of SLA health across all active work.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'sla_status', label: 'SLA Status'}],
        defaultSort: 'sla_time_left ASC',
        defaultJql: 'resolution = Unresolved AND slaFunction("breached")'
    },
    {
        id: 'Q_MGR_04',
        name: 'Chronic Breach Patterns',
        audienceRole: 'Manager',
        intentCategory: 'management',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Breached items grouped by Assignee/Category',
        description: 'Analysis view to identify recurring failure patterns.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'created', label: 'Created'}],
        defaultSort: 'created DESC',
        defaultJql: 'slaFunction("breached") ORDER BY assignee ASC'
    },
    {
        id: 'Q_EXEC_01',
        name: 'Operational Risk Snapshot',
        audienceRole: 'Executive',
        intentCategory: 'executive',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'High Priority AND Unresolved',
        description: 'Executive summary of critical operational risks.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'priority', label: 'Priority'}, {field: 'status', label: 'Status'}],
        defaultSort: 'priority DESC',
        defaultJql: 'priority in (High, Critical) AND resolution = Unresolved'
    },
    // Quality / Diagnostic
    {
        id: 'Q_QA_01',
        name: 'Tickets Reopened After Resolution',
        audienceRole: 'Quality Assurance',
        intentCategory: 'quality',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Status changed from Done to Open AND Unresolved',
        description: 'Quality control queue for investigating premature closures.',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'status', label: 'Status'}],
        defaultSort: 'updated DESC',
        defaultJql: 'status WAS "Done" AND resolution = Unresolved'
    },
    {
        id: 'Q_QA_02',
        name: 'High Reassignment Count',
        audienceRole: 'Quality Assurance',
        intentCategory: 'quality',
        domain: 'cross_domain',
        maturityLevel: 'advanced',
        canonicalFilterLogic: 'Unresolved AND Reassignment Count > Threshold',
        description: 'Identifies tickets being bounced between teams (ping-pong effect).',
        defaultColumns: [{field: 'key', label: 'Key'}, {field: 'summary', label: 'Summary'}, {field: 'assignee', label: 'Assignee'}, {field: 'updated', label: 'Updated'}],
        defaultSort: 'updated DESC',
        defaultJql: 'resolution = Unresolved'
    }
];
